import { TemplateResult, html, css } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement } from 'lit/decorators.js';
import { debounce } from '../../../utilities/debounce';

/**
 * The Score image component
 * @element tufts-responsive-image
 * @slot - default
 */
@customElement('tufts-responsive-image')
export class tuftsResponsiveImage extends OutlineElement {
  /**
   * From the spec (http://www.w3.org/TR/cssom-view/#dom-element-clientwidth)
   *
   * The clientWidth attribute must run these steps:
   * If the element has no associated CSS layout box or if the CSS layout box is inline, return zero.
   *
   * :host (parent of img) is given display: block, so it has a layout box
   */
  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
  `;

  firstUpdated() {
    this.resizer();
  }

  // code adopted to TS from easy_responsive_images JS file - https://git.drupalcode.org/project/easy_responsive_images/-/blob/1.3.x/js/resizer.js
  resizer() {
    // Fetch all images containing a "data-srcset" attribute.
    const images = this.querySelectorAll('img[data-srcset]');

    // Return early if no images were found.
    if (!images || images.length === 0) {
      return;
    }

    // Find the best suitable image to display.
    const updateImage = function (image: HTMLImageElement) {
      // Check if we need to apply a multiplier to the width check and load
      // bigger images.
      let multiplier = 1;
      const dataMultiplier = image.getAttribute('data-multiplier');
      if (dataMultiplier !== null) {
        multiplier = parseFloat(dataMultiplier.replace('x', ''));
      }
      const multiplierMax = multiplier >= 1 ? multiplier : 1;
      const multiplierFactor =
        window.devicePixelRatio < multiplierMax
          ? window.devicePixelRatio
          : multiplierMax;

      // Check if we need to use the container height to select the best
      // image.
      const cover = image.hasAttribute('data-cover');

      // Check the available width for the image based on the multiplier.
      const imgWidth = Math.floor(image.clientWidth);
      const imgHeight = Math.floor(image.clientHeight);
      const parentWidth = Math.floor(
        (image.parentNode as HTMLElement).clientWidth
      );
      const parentHeight = Math.floor(
        (image.parentNode as HTMLElement).clientHeight
      );
      const availableWidth = parentWidth > imgWidth ? parentWidth : imgWidth;
      const availableHeight =
        parentHeight > imgHeight ? parentHeight : imgHeight;
      const targetWidth = multiplierFactor * availableWidth;
      const targetHeight = multiplierFactor * availableHeight;

      // Get the current width and height of the image.
      const attrWidth = image.getAttribute('width');
      const attrHeight = image.getAttribute('height');

      // Get all the source values and the current image source.
      const sources = image.getAttribute('data-srcset')?.split(',');
      const currentSrc = image.getAttribute('src');

      // Fetch the image ratio if it is available.
      let ratioWidth: string | undefined, ratioHeight: string | undefined;
      const ratio = image.getAttribute('data-ratio');
      if (ratio) {
        [ratioWidth, ratioHeight] = ratio.split(':');
      }

      // If the selected image has a src attribute, and is already bigger than
      // the available width, we do not update the image.
      if (
        image.src &&
        attrWidth &&
        parseInt(attrWidth) > availableWidth &&
        attrHeight &&
        parseInt(attrHeight) > availableHeight
      ) {
        image.setAttribute('data-loaded', '1');
        return;
      }

      // Find the best matching source based on actual image space.
      let source, responsiveImgPath, responsiveImgWidth, responsiveImgHeight;
      for (source of sources || []) {
        const array = source.split(' ');
        responsiveImgPath = array[0];
        responsiveImgWidth = array[1].slice(0, -1);
        if (cover && parentWidth && parentHeight) {
          responsiveImgHeight =
            (parseInt(responsiveImgWidth) * parentWidth) / parentHeight;
        } else if (ratioWidth && ratioHeight) {
          responsiveImgHeight =
            (parseInt(responsiveImgWidth) * parseInt(ratioHeight)) /
            parseInt(ratioWidth);
        } else if (attrWidth && attrHeight) {
          responsiveImgHeight =
            (parseInt(responsiveImgWidth) * parseInt(attrHeight)) /
            parseInt(attrWidth);
        }
        // Stop looking if the image is bigger than the target width and the
        // image height is bigger than the target height.
        if (
          targetWidth < parseInt(responsiveImgWidth) &&
          (!cover ||
            !responsiveImgHeight ||
            targetHeight < parseInt(responsiveImgHeight.toString()))
        ) {
          break;
        }
      }

      // If the image is already loaded, we do not update the image.
      if (responsiveImgPath === currentSrc) {
        image.setAttribute('data-loaded', '1');
        return;
      }

      // Set a temporary attribute to prevent multiple observers from updating
      // the image simultaneously.
      image.setAttribute('data-loading', '1');

      // Update the "src" with the new image path.
      // Ensure responsiveImgPath is defined before setting the image source
      if (responsiveImgPath !== undefined) {
        image.src = responsiveImgPath;
      }

      // Calculate the height from the dimensions of the initial width and
      // height since this can also cause layout shifts.
      image.setAttribute('width', availableWidth.toString());
      if (cover && parentWidth && parentHeight) {
        image.setAttribute(
          'height',
          Math.round((availableWidth * parentHeight) / parentWidth).toString()
        );
      } else if (ratioWidth && ratioHeight) {
        image.setAttribute(
          'height',
          Math.round(
            (availableWidth * parseInt(ratioHeight)) / parseInt(ratioWidth)
          ).toString()
        );
      } else if (attrWidth && attrHeight) {
        image.setAttribute(
          'height',
          Math.round(
            (availableWidth * parseInt(attrHeight)) / parseInt(attrWidth)
          ).toString()
        );
      }

      // Set the "data-loaded" attribute when the image is loaded.
      image.onload = () => {
        image.setAttribute('data-loaded', '1');

        // Remove the temporary attribute when the image finishes loading.
        image.removeAttribute('data-loading');

        // Update the image width/height onload to the actual image dimensions.
        if (cover || !ratioWidth || !ratioHeight) {
          image.setAttribute('width', Math.floor(image.width).toString());
          image.setAttribute('height', Math.floor(image.height).toString());
        }
      };
    };

    // Create a ResizeObserver to update the image "src" attribute when its
    // parent container resizes.
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const images = entry.target.querySelectorAll('img[data-srcset]');
        images.forEach(image => {
          // Call the debounced version of updateImage instead of updateImage directly.
          // This ensures that updateImage is only called once, 200 milliseconds after the last resize event.
          debouncedUpdateImage(image as HTMLImageElement);
        });
      }
    });

    images.forEach(image => {
      // Attach the ResizeObserver to the image container.
      if (image.parentNode instanceof Element) {
        resizeObserver.observe(image.parentNode);
      }
    });

    // Single instance create a debounced version of the updateImage function.
    const debouncedUpdateImage = debounce(updateImage, 200);

    /**
     * @TODO: <img> in shadowDOM do not intersect, until that gets resolved, this code is commented out.
     *
     **/
    /*
    // Attach the ResizeObserver and IntersectionObserver to the images.
    images.forEach(image => {
      // Make sure we only attach the observers once.
      if (image.hasAttribute('data-observed')) {
        return;
      }

      // Mark that the image the observers are attached.
      image.setAttribute('data-observed', '1');

      // Attach the IntersectionObserver to the image if it is using lazy
      // loading, otherwise attach the ResizeObserver.
      if (
        image.hasAttribute('loading') &&
        image.getAttribute('loading') === 'lazy'
      ) {
        // When an image is using lazy loading, we unset the src to prevent it
        // from being loaded directly. This prevents the browser from lazy
        // loading the default image before we get the chance to set the
        // correct src.
        image.removeAttribute('src');

        // Attach the IntersectionObserver to the image.
        intersectionObserver.observe(image);
      } else {
        // Load the correct image directly.
        if (image instanceof HTMLImageElement) {
          updateImage(image);
        }

        // Attach the ResizeObserver to the image container.
        if (image.parentNode instanceof Element) {
          resizeObserver.observe(image.parentNode);
        }
      }
    });
    */
  }

  render(): TemplateResult {
    return html` <slot></slot> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-responsive-image': tuftsResponsiveImage;
  }
}
